import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { useCompany, useAlertStore } from '../../stores/index';
import { useAddUserMutation } from '../../stores/FetcherStores/UserFetcher';

const Users = () => {
  const { t } = useTranslation();
  const companyStore = useCompany();
  const company = companyStore.data;
  const alertStore = useAlertStore();

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [existingUserEmail, setExistingUserEmail] = useState('');

  const { mutateAsync: addUser, isLoading } = useAddUserMutation();

  const handleCreateUser = () => {
    if (!companyStore.companyKey) return;

    addUser(companyStore.companyKey, newUserEmail, newUserPassword)
      .then(() => {
        alertStore.pushAlert({
          message: 'User created successfully',
          severity: 'success',
        });
        setNewUserEmail('');
        setNewUserPassword('');
      })
      .catch((error: unknown) => {
        alertStore.pushAlert({
          message: error instanceof Error ? error.message : 'Failed to create user',
          severity: 'error',
        });
      });
  };

  const handleAddExistingUser = () => {
    if (!companyStore.companyKey) return;

    addUser(companyStore.companyKey, existingUserEmail)
      .then(() => {
        alertStore.pushAlert({
          message: 'User added successfully',
          severity: 'success',
        });
        setExistingUserEmail('');
      })
      .catch((error: unknown) => {
        if (error instanceof AxiosError && error.response?.status === 404) {
          alertStore.pushAlert({
            message: 'User not found',
            severity: 'error',
          });
        } else {
          alertStore.pushAlert({
            message: error instanceof Error ? error.message : 'Failed to add user',
            severity: 'error',
          });
        }
      });
  };

  if (!company) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        {t('userManagement.title')}
      </Typography>

      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '2rem' }}>
        <Typography variant="h6" gutterBottom>
          {t('userManagement.createNewUser')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label={t('userManagement.email')}
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              type="password"
              label={t('userManagement.password')}
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={isLoading || !newUserEmail || !newUserPassword}
              fullWidth
              sx={{ height: '100%' }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('userManagement.createUser')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '2rem' }}>
        <Typography variant="h6" gutterBottom>
          {t('userManagement.addExistingUser')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={10}>
            <TextField
              fullWidth
              label={t('userManagement.email')}
              value={existingUserEmail}
              onChange={(e) => setExistingUserEmail(e.target.value)}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              onClick={handleAddExistingUser}
              disabled={isLoading || !existingUserEmail}
              fullWidth
              sx={{ height: '100%' }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('userManagement.addUser')}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Users;
